import React, { useMemo } from 'react';
import { Tabs as CLTabs } from '@fortress-technology-solutions/fortress-component-library/Organisms';
import { FormattedMessage } from 'react-intl';
import messages from './messages';
import TextingTabIcon from '../../containers/TextingTabIcon';
import { TAB_KEYS } from './constants';
import { filterAndDeleteShowPropFromTabs } from './utils';
import { showHousingAssistance } from '../../utils/housing-assistance-helpers';
import { useProviderPropertyProfiles } from '../ApplicationProfile/LeaseApplicationTab/hooks.queries';
import { useActiveProviderPropertyProfiles } from '../../components/CreditCriminalStatusScreening/hooks';

const Tabs = ({
  affordableTabs,
  checkUserPermissions,
  flags,
  householdId,
  hudTab,
  id,
  isCommercial,
  onChange,
  programs,
  prospectInfoId,
  selectedProperty,
  showAffordableTabs,
  showCamTab,
  showDocumentsTab,
  showHUD,
  showTextingTab,
  value,
}) => {
  const { providerPropertyProfiles } = useProviderPropertyProfiles({
    propertyId: selectedProperty?.id,
    enabled: flags?.renewalScreenings === true && Boolean(selectedProperty?.id),
  });
  const activeProviderPropertyProfiles = useActiveProviderPropertyProfiles(
    providerPropertyProfiles ?? [],
  );
  const hasActiveScreeningProvider =
    (activeProviderPropertyProfiles?.length ?? 0) > 0;

  const shouldShowHousingAssistance = showHousingAssistance({
    property: selectedProperty,
    isSubrentEnabled: true,
    hasCommercialFloorPlans: isCommercial
      ? 'ALL' // override setting if the selected unit is commercial
      : selectedProperty?.hasCommercialFloorPlans,
    hide: programs.isHUD || programs.isRD || programs.isPH,
  });

  const tabs = useMemo(() => {
    return filterAndDeleteShowPropFromTabs([
      {
        label: <FormattedMessage {...messages.activityTab} />,
        value: TAB_KEYS.ACTIVITY,
        disabled: !checkUserPermissions(['activity-read']),
      },
      {
        label: 'Work Orders',
        value: TAB_KEYS.WORK_ORDERS,
        disabled: !checkUserPermissions(['activity-read']),
      },
      {
        label: <FormattedMessage {...messages.texting} />,
        value: TAB_KEYS.TEXTING,
        icon: <TextingTabIcon householdId={householdId} />,
        show: showTextingTab,
      },
      {
        label: <FormattedMessage {...messages.ledgerTab} />,
        value: TAB_KEYS.LEDGER,
      },
      {
        label: <FormattedMessage {...messages.leaseDataTab} />,
        value: TAB_KEYS.LEASE_DATA,
      },
      {
        label: <FormattedMessage {...messages.housingAssistanceVoucherTab} />,
        value: TAB_KEYS.HOUSING_ASSISTANCE_VOUCHER,
        show: flags?.housingChoiceVouchers && shouldShowHousingAssistance,
      },
      {
        label: <FormattedMessage {...messages.camTab} />,
        value: TAB_KEYS.CAM,
        show: showCamTab,
      },
      {
        label: <FormattedMessage {...messages.applicationTab} />,
        value: TAB_KEYS.LEASE_APPLICATION,
        show: !isCommercial,
      },
      {
        label: <FormattedMessage {...messages.screeningTab} />,
        value: TAB_KEYS.SCREENING,
        show: flags?.renewalScreenings === true && hasActiveScreeningProvider,
      },
      {
        label: <FormattedMessage {...messages.documentsTab} />,
        value: TAB_KEYS.DOCUMENTS,
        show: showDocumentsTab,
      },
      {
        label: <FormattedMessage {...messages.prospectInformation} />,
        value: TAB_KEYS.PROSPECT_INFO,
        show: Boolean(prospectInfoId?.length) && !isCommercial,
      },
      ...(showAffordableTabs
        ? affordableTabs?.map(
            ({ programName, combinedName, urlProgramName }) => ({
              label: `${combinedName ?? programName} Qualification`,
              value: TAB_KEYS[urlProgramName.toUpperCase()] || urlProgramName,
            }),
          )
        : []),
      {
        label: `${hudTab?.programName} Qualification`,
        value: TAB_KEYS.HUD,
        show: showHUD,
      },
    ]);
  }, [
    affordableTabs,
    checkUserPermissions,
    flags,
    householdId,
    hudTab,
    isCommercial,
    prospectInfoId,
    showAffordableTabs,
    showCamTab,
    showDocumentsTab,
    showHUD,
    showTextingTab,
    shouldShowHousingAssistance,
    hasActiveScreeningProvider,
  ]);

  const tabsKey = useMemo(() => tabs?.map((t) => t.value).join('|'), [tabs]);

  return (
    <CLTabs
      key={tabsKey}
      id={id}
      value={value}
      onChange={(_, value) => onChange(value)}
      sx={{ mb: 1, borderBottom: '1px solid', borderColor: 'divider' }}
      scrollButtons="auto"
      variant="scrollable"
      tabs={tabs}
    />
  );
};

export default Tabs;
